import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { rephraseHandler, continueWritingHandler, makeShorterHandler, makeLongerHandler } from 'redux/toolbar';
import ToolbarSuggestionActions from './ToolbarSuggestionActions';

const ToolbarSuggestion = ({
  onInsert,
  onDiscard
}) => {
  const [displayActions, setDisplayActions] = useState(false);

  /* Helpers */
  const dispatch = useDispatch();

  /* Selectors */
  const suggestionText = useSelector((state) => state.toolbar.suggestion);
  const suggestionStream = useSelector((state) => state.toolbar.suggestionStream);
  const selectedText = useSelector((state) => state.toolbar.selectedText);

  /* Functions */
  const rephraseCurrentHandler = useCallback(() => {
    dispatch(rephraseHandler(suggestionText));
  }, [dispatch, suggestionText]);

  const continueWritingCurrentHandler = useCallback(() => {
    dispatch(continueWritingHandler(suggestionText, { continue: true }));
  }, [dispatch, suggestionText]);

  const copyHandler = useCallback(() => {
    navigator.clipboard.writeText(suggestionText);
  }, [suggestionText]);

  const shorterHandler = useCallback(() => {
    dispatch(makeShorterHandler(suggestionText));
  }, [dispatch, suggestionText]);

  const longerHandler = useCallback(() => {
    dispatch(makeLongerHandler(suggestionText));
  }, [dispatch, suggestionText]);

  const insertHandler = useCallback(() => {
    onInsert({ suggestionText });
  }, [onInsert, suggestionText]);

  const replaceSelectionHandler = useCallback(() => {
    onInsert({ suggestionText });
  }, [onInsert, suggestionText]);

  const insertBellowHandler = useCallback(() => {
    onInsert({ suggestionText, bellow: true });
  }, [onInsert, suggestionText]);

  const discardHandler = useCallback(() => {
    onDiscard();
  }, [onDiscard]);

  const hasSelection = !!selectedText.length;

  useEffect(() => {
    if (suggestionStream.length) {
      setDisplayActions(true);
    }
  }, [suggestionStream]);

  /* Render */
  if (!suggestionText.length) {
    return <></>
  }

  return (
    <Wrapper>
      <Text>
        {suggestionText}
      </Text>
      {displayActions && (
        <ToolbarSuggestionActions
          hasSelection={hasSelection}
          onInsert={insertHandler}
          onInsertBellow={insertBellowHandler}
          onDiscard={discardHandler}
          onReplaceSelection={replaceSelectionHandler}
          onMakeLonger={longerHandler}
          onRephrase={rephraseCurrentHandler}
          onContinueWriting={continueWritingCurrentHandler}
          onCopy={copyHandler}
          onMakeShorter={shorterHandler}
        />
      )}
    </Wrapper>
  );
}

export default ToolbarSuggestion;

ToolbarSuggestion.propTypes = {
  onInsert: PropTypes.func,
  onDiscard: PropTypes.func,
};

ToolbarSuggestion.defaultProps = {
  onInsert: () => {},
  onDiscard: () => {},
};

const Wrapper = styled.div`
  padding: 6px;
  margin: 6px;
  font-size: 14px;
  font-family: 'Inter';
  border-top: 1px solid var(--lizzy-black-10);
`;

const Text = styled.div`
  cursor: text;
  white-space: pre-wrap;
`;